import { useQuery } from '@tanstack/react-query';
import { getLeaderCategoriesByLanguage } from 'apis/leaderCategories';
import { getLeadersByLanguage } from 'apis/leaders';
import { allLeadersList } from 'localData/leaderShip';
import { leaderCategorys as defaultLeaderCategorys } from 'localData/leaderShip/leaderShip';

const fallbackCategories = defaultLeaderCategorys.map(category => ({
    id: category.id,
    name: category.categoryName,
    slug: category.slug,
    sortOrder: category.id,
}));

const getFallbackLeaders = (languageCode) => allLeadersList[languageCode] || allLeadersList.vi || [];

export const leadershipKeys = {
    all: ['leadership'],
    leaders: (languageCode) => ['leadership', 'leaders', languageCode],
    categories: (languageCode) => ['leadership', 'categories', languageCode],
    overview: (languageCode) => ['leadership', 'overview', languageCode],
    pageMeta: (languageCode) => ['leadership', 'page-meta', languageCode],
};

export const useLeaderCategoriesQuery = (languageCode) => (
    useQuery({
        queryKey: leadershipKeys.categories(languageCode),
        queryFn: async () => {
            try {
                const categories = await getLeaderCategoriesByLanguage(languageCode);
                return (categories || []).filter(item => item.isActive !== false);
            } catch {
                return fallbackCategories;
            }
        },
        enabled: Boolean(languageCode),
    })
);

export const useLeadersQuery = (languageCode) => (
    useQuery({
        queryKey: leadershipKeys.leaders(languageCode),
        queryFn: async () => {
            try {
                return await getLeadersByLanguage(languageCode);
            } catch {
                return getFallbackLeaders(languageCode);
            }
        },
        enabled: Boolean(languageCode),
    })
);

export const useLeadershipOverviewQuery = (languageCode) => (
    useQuery({
        queryKey: leadershipKeys.overview(languageCode),
        queryFn: async () => {
            const [leaders, categories] = await Promise.all([
                getLeadersByLanguage(languageCode).catch(() => getFallbackLeaders(languageCode)),
                getLeaderCategoriesByLanguage(languageCode).catch(() => fallbackCategories),
            ]);

            return {
                leaders: leaders || [],
                categories: (categories || []).filter(item => item.isActive !== false),
            };
        },
        enabled: Boolean(languageCode),
    })
);

export const useLeadershipPageMetaQuery = (languageCode) => (
    useQuery({
        queryKey: leadershipKeys.pageMeta(languageCode),
        queryFn: async () => {
            try {
                const module = await import(`../language/pages_${languageCode}`);
                const pageData = module[`pages_${languageCode}`];

                return {
                    pageName: pageData.introduce.leadershipPageName || 'Giới thiệu',
                    titleCenter: pageData.introduce.leadershipTitle || 'Ban lãnh đạo',
                };
            } catch {
                return {
                    pageName: 'Giới thiệu',
                    titleCenter: 'Ban lãnh đạo',
                };
            }
        },
        enabled: Boolean(languageCode),
    })
);
